'use client';

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Embed players shared by the popup's video-shaped renderers
 * ({@code VideoRenderer}, {@code ShortsRenderer}). Neither provider
 * exposes an "ended" event to the parent frame, so both mark complete
 * on mount — the user has clearly chosen to watch, and losing the
 * progress write on an early close would be worse.
 */

export function YouTubeEmbed({
  embedUrl,
  onComplete,
}: {
  embedUrl: string;
  onComplete: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  // YouTube's iframe doesn't expose "ended" without the IFrame Player
  // API. Mark complete on mount so progress isn't lost when the user
  // closes the popup mid-watch (matches legacy popup behaviour: any
  // YouTube view counts as completion intent).
  useEffect(() => {
    onComplete();
    // onComplete intentionally not in deps — fire once on mount only.
  }, []);
  return (
    <iframe
      src={`${embedUrl}?rel=0&modestbranding=1&autoplay=1`}
      title={t('microEmbeds.video.title')}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
    />
  );
}

/**
 * Cinema8 player. An interactive scene mounts the {@code <cinema8-player>} web
 * component; a raw media ({@code raw}) plays in Cinema8's raw-video iframe, the
 * same embed the course detail page uses for C8Video previews.
 */
export function Cinema8Embed({
  mediaId,
  raw,
  posterUrl,
  onComplete,
}: {
  mediaId: string;
  raw: boolean;
  posterUrl: string | undefined;
  onComplete: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (raw || !ref.current) return;
    const safeId = mediaId.replace(/"/g, '');
    const posterAttr = posterUrl ? ` poster="${posterUrl.replace(/"/g, '')}"` : '';
    ref.current.innerHTML =
      `<cinema8-player media-id="${safeId}"${posterAttr} ` +
      `style="position:absolute;inset:0;width:100%;height:100%;border:0"></cinema8-player>`;
  }, [mediaId, posterUrl, raw]);

  // Ensure the player script is on the page. Idempotent — only adds once
  // even if multiple Cinema8 embeds mount on the same page.
  useEffect(() => {
    if (raw || typeof document === 'undefined') return;
    if (document.querySelector('script[data-cinema8-player]')) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://static-01.cinema8.com/embed/player.js';
    s.setAttribute('data-cinema8-player', '1');
    document.head.appendChild(s);
  }, [raw]);

  // Cinema8's web component doesn't expose "ended" to React; treat
  // mount as completion intent (same rationale as YouTube above).
  useEffect(() => {
    onComplete();
  }, []);

  if (raw) {
    return (
      <iframe
        src={`https://cinema8.com/raw-video/${encodeURIComponent(mediaId)}?autoplay=1`}
        title={t('microEmbeds.video.title')}
        loading="lazy"
        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    );
  }
  return <div ref={ref} style={{ position: 'relative', width: '100%', height: '100%' }} />;
}
